import { useEffect, useState } from "react";
import Layout from "../../layout.tsx/app";
import { useManageAsesmenStore } from "../manage-asesmen.store";
import { MagnifyingGlass, Pencil, Trash, X } from "@phosphor-icons/react";
import { Spinner } from "flowbite-react";
import Pagination from "../../../components/tables/pagination";
import AddButton from "../../../components/buttons/add";
import Table from "../../../components/tables/base";
import { AssessmentRegion, PsikologRegion } from "../manage-asesmen";
import { SelectOptionType } from "../../../types/form";
import { FormProvider, useForm } from "react-hook-form";
import moment from "moment";
import BaseModal from "../../../components/modal/base";
import { FormInput, FormInputPassword } from "../../../components/forms/input";
import {
  FormSelectAsync,
  FormSelectTimezone,
} from "../../../components/forms/input-select";
import { Button } from "../../../components/buttons";
import { request } from "../../../api/config";
import { useAlert } from "../../../stores/alert";
import { generatePassword } from "../../../helper/password";
import ModalDeleteConfirmation from "../../../components/modal/delete-confirmation";

type FormValues = {
  name: string;
  email: string;
  timezone: SelectOptionType | undefined;
  region_id: AssessmentRegion | undefined;
  password: string;
};

type ErrorForm = {
  name: [] | null;
  email: [] | null;
  timezone: [] | null;
  region_id: [] | null;
  password: [] | null;
};

const ManageOfflinePsikolog = () => {
  const [page, setPage] = useState<number>(1);
  const [name, setName] = useState<string>("");
  const [modal, setModal] = useState<boolean>(false);
  const [modalDelete, setModalDelete] = useState<boolean>(false);
  const [modalMode, setModalMode] = useState<"edit" | "create" | undefined>(
    undefined
  );
  const [selected, setSelected] = useState<PsikologRegion | null>(null);
  const [errors, setErrors] = useState<ErrorForm | null>(null);
  const [loadingSubmit, setLoadingSubmit] = useState<boolean>(false);

  const { psikologs, getPsikologs, loadingPsikologs } = useManageAsesmenStore();
  const { setMessage } = useAlert();

  const forms = useForm<FormValues>({
    defaultValues: {
      timezone: { label: moment.tz.guess(), value: moment.tz.guess() },
      password: generatePassword(8),
    },
  });

  const handleSearch = async () => {
    getPsikologs({ page, name });
  };

  const handleNext = () => {
    if (page === psikologs?.last_page) {
      return;
    }

    setPage(page + 1);
  };

  const handlePrevious = () => {
    if (page === 1) {
      return;
    }

    setPage(page - 1);
  };

  const handleFormEdit = (item: PsikologRegion) => {
    setSelected(item);
    setModalMode("edit");

    forms.setValue("name", item.name ?? "");
    forms.setValue("email", item.email);
    forms.setValue("timezone", { label: item.timezone, value: item.timezone });
    forms.setValue("region_id", item.region);

    setModal(true);
  };

  const selectRegion = async (inputValue: string) => {
    let params = {
      q: inputValue,
    };
    const { data } = await request.get("/manage-asesmen/regions", {
      params: params,
    });
    return data.data.data;
  };

  const handleSave = forms.handleSubmit(async (data) => {
    setLoadingSubmit(true);
    try {
      if (modalMode === "create") {
        await request.post("/manage-asesmen/external-psikolog", {
          ...data,
          region_id: data.region_id?.id,
        });
      } else {
        await request.put(`/manage-asesmen/external-psikolog/${selected?.id}`, {
          name: data.name,
          email: data.email === selected?.email ? undefined : data.email,
          region_id: data.region_id?.id,
          timezone: data.timezone?.value,
        });
      }
      setModal(false);
      setErrors(null);
      setModalMode(undefined);
      setMessage("Psikolog user saved!", "success");
      getPsikologs();
    } catch (err: any) {
      // console.log(err.response.data.errors);
      setErrors(err.response.data.errors);
      console.log(err);
    } finally {
      // setErrors(null);
      setLoadingSubmit(false);
    }
  });

  const handleDelete = async () => {
    setLoadingSubmit(true);
    try {
      await request.delete(`/users/psikolog/${selected?.id}/destroy`);
      setSelected(null);
      setModalDelete(false);
      setMessage("User psikolog deleted", "success");
      getPsikologs();
    } catch (err: any) {
      setErrors(err.response.data.errors);
    }
    setLoadingSubmit(false);
  };

  useEffect(() => {
    getPsikologs({ page, name });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getPsikologs, page]);

  return (
    <>
      <Layout
        withPageTitle
        title="Manajemen Psikolog Offline"
        pageTitleContent={
          <div className="flex items-center">
            <input
              type="text"
              className="rounded-l-lg border-gray-300"
              placeholder={"Cari disini..."}
              onChange={(e) => setName(e.target.value)}
              disabled={loadingPsikologs}
              value={name}
            />
            {name && (
              <button
                onClick={() => {
                  setName("");
                  getPsikologs({ page, name: "" });
                }}
                className="py-3 px-2 border border-red-600 bg-red-600 text-white"
              >
                <X />
              </button>
            )}
            <button
              className={`${
                loadingPsikologs ? "py-2 px-3" : "p-3"
              } text-lg rounded-r-lg ${
                loadingPsikologs
                  ? "bg-blue-500 text-white cursor-not-allowed"
                  : "bg-blue-600 text-white hover:bg-blue-700"
              }`}
              disabled={loadingPsikologs}
              onClick={handleSearch}
            >
              {loadingPsikologs ? <Spinner size={"sm"} /> : <MagnifyingGlass />}
            </button>
          </div>
        }
      >
        <AddButton
          onClick={() => {
            setModal(true);
            setModalMode("create");
            forms.reset();
          }}
        />
        <Table>
          <Table.Thead>
            <Table.Th>#</Table.Th>
            <Table.Th>Nama</Table.Th>
            <Table.Th>Email</Table.Th>
            <Table.Th>Region</Table.Th>
            <Table.Th className="text-center">Opsi</Table.Th>
          </Table.Thead>
          <Table.Tbody>
            {loadingPsikologs ? (
              <Table.TrLoading cols={5} rows={4} />
            ) : (
              <>
                {psikologs?.data.length === 0 ? (
                  <Table.Tr>
                    <Table.Td cols={5} className="text-center py-3">
                      Tidak ada data ditemukan!
                    </Table.Td>
                  </Table.Tr>
                ) : (
                  <>
                    {psikologs?.data.map((item, key) => (
                      <Table.Tr key={key}>
                        <Table.Td>
                          {(
                            key +
                            1 +
                            psikologs.per_page * (psikologs.current_page - 1)
                          ).toString()}
                        </Table.Td>
                        <Table.Td>{item.name ?? ""}</Table.Td>
                        <Table.Td>{item.email ?? ""}</Table.Td>

                        <Table.Td>{item.region?.name ?? "-"}</Table.Td>

                        <Table.Td>
                          <div className="flex items-center justify-center gap-1">
                            <Trash
                              className="text-red-600 text-xl cursor-pointer"
                              onClick={() => {
                                setSelected(item);
                                setModalDelete(true);
                              }}
                            />
                            <Pencil
                              className="text-blue-600 text-xl cursor-pointer"
                              onClick={() => handleFormEdit(item)}
                            />
                          </div>
                        </Table.Td>
                      </Table.Tr>
                    ))}
                  </>
                )}
              </>
            )}
          </Table.Tbody>
        </Table>
        <Pagination
          currentPage={psikologs?.current_page ?? 1}
          totalPage={psikologs?.last_page ?? 1}
          onNext={handleNext}
          onPrevious={handlePrevious}
        />
      </Layout>

      <BaseModal
        title={
          modalMode === "create"
            ? "Tambah Psikolog Offline"
            : "Edit Psikolog Offline"
        }
        isOpen={modal}
        close={() => setModal(false)}
      >
        <FormProvider {...forms}>
          <form>
            <FormInput
              name="name"
              control={forms.control}
              label="Nama"
              error={errors?.name}
            />
            <FormInput
              name="email"
              control={forms.control}
              label="Email"
              error={errors?.email}
            />
            <>
              {modalMode === "create" && (
                <>
                  <FormInputPassword
                    name="password"
                    control={forms.control}
                    defaultValue={""}
                    label="Password"
                    type="password"
                    error={errors?.password}
                  />
                  {/* <div className="mb-4 flex items-center justify-end">
                    <div
                      className=" bg-blue-600 p-2 text-xs text-white rounded-lg inline cursor-pointer"
                      onClick={handleSetDefaultPass}
                    >
                      <span>Generate Password</span>
                    </div>
                  </div> */}
                </>
              )}
            </>
            <FormSelectAsync
              label="Pilih Region"
              name="region_id"
              control={forms.control}
              loadOption={selectRegion}
              required
              optionLabel={(option: AssessmentRegion) => `${option.name}`}
              optionValue={(option: AssessmentRegion) => `${option.id}`}
              error={errors?.region_id}
            />
            <FormSelectTimezone
              name="timezone"
              control={forms.control}
              label="Zona Waktu"
              error={errors?.timezone}
            />

            <div className="mt-3 flex items-center justify-end">
              <Button className="px-8" onClick={handleSave}>
                {loadingSubmit ? <Spinner /> : "Simpan"}
              </Button>
            </div>
          </form>
        </FormProvider>
      </BaseModal>

      <ModalDeleteConfirmation
        isOpen={modalDelete}
        close={() => setModalDelete(false)}
        name={selected?.name ?? ""}
        loading={loadingSubmit}
        action={handleDelete}
      />
    </>
  );
};

export default ManageOfflinePsikolog;
